import { motion, useReducedMotion } from 'motion/react'
import { ScreenShell } from '@/components/layout/ScreenShell'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { useNavigation } from '@/contexts/NavigationProvider'
import { useProgress } from '@/contexts/ProgressProvider'
import { BIOMES, BIOME_ORDER, type BiomeId } from '@/styles/theme'
import { LEVELS, type LevelMeta } from '@/game/levels'
import { biomeArt } from '@/game/art'
import { buildBiomeMapCraft, type BiomeMapCraft, type MapCraftBridge, type MapCraftNode } from '@/game/worldcraft'

function levelsByBiome(id: BiomeId): LevelMeta[] {
  return LEVELS.filter((l) => l.biome === id)
}

function MapBridge({ bridge, biome }: { bridge: MapCraftBridge; biome: (typeof BIOMES)[BiomeId] }) {
  const dx = bridge.to.x - bridge.from.x
  const dy = bridge.to.y - bridge.from.y
  const len = Math.hypot(dx, dy)
  const angle = Math.atan2(dy, dx) * (180 / Math.PI)
  return (
    <span
      className="absolute h-2 rounded-full shadow-sunken"
      style={{
        left: `${bridge.from.x}%`,
        top: `${bridge.from.y}%`,
        width: `${len}%`,
        transform: `rotate(${angle}deg) translateY(-50%)`,
        transformOrigin: '0 50%',
        background: bridge.unlocked
          ? `linear-gradient(90deg, ${biome.biome}, ${biome.biomeSoft})`
          : 'linear-gradient(90deg, rgba(255,255,255,0.1), rgba(255,255,255,0.04))',
        opacity: bridge.unlocked ? 0.78 : 0.38,
      }}
    />
  )
}

function MapNode({
  node,
  level,
  biome,
  onStart,
  compact,
}: {
  node: MapCraftNode
  level: LevelMeta
  biome: (typeof BIOMES)[BiomeId]
  onStart: (index: number) => void
  compact: boolean
}) {
  return (
    <button
      disabled={!node.unlocked}
      onClick={() => onStart(level.index)}
      aria-label={`${level.name}${node.stars ? `, ${node.stars} stars` : ''}`}
      title={level.name}
      className={[
        'absolute grid -translate-x-1/2 -translate-y-1/2 place-items-center rounded-[var(--r-sm)] font-black shadow-emboss transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-55',
        compact ? 'h-8 w-8 text-[11px]' : 'h-14 w-14 text-sm',
      ].join(' ')}
      style={{
        left: `${node.x}%`,
        top: `${node.y}%`,
        background: node.completed ? `linear-gradient(135deg, ${biome.biomeSoft}, ${biome.biome})` : 'var(--surface-sunken)',
        color: node.completed ? biome.ink : 'var(--text-dim)',
        boxShadow: node.unlocked ? `0 0 0 2px ${biome.edge}, var(--shadow-emboss)` : 'var(--shadow-emboss)',
      }}
    >
      {node.unlocked ? node.order + 1 : <PixelIcon name="lock" size={14} />}
      {node.stars > 0 && (
        <span
          className={['absolute right-0 top-0 grid place-items-center rounded-full px-1 font-black leading-none', compact ? 'h-3 min-w-3 text-[7px]' : 'h-4 min-w-4 text-[9px]'].join(' ')}
          style={{ background: 'var(--coin)', color: '#231404' }}
        >
          {node.stars}
        </span>
      )}
    </button>
  )
}

function MapChip({ chip, biomeId, biome, unlocked }: { chip: BiomeMapCraft['chips'][number]; biomeId: BiomeId; biome: (typeof BIOMES)[BiomeId]; unlocked: boolean }) {
  const glow = Math.round(32 + chip.tone * 52).toString(16).padStart(2, '0')
  const baseStyle = {
    left: `${chip.x}%`,
    top: `${chip.y}%`,
    borderRadius: chip.mask === 15 ? '35%' : chip.mask === 5 || chip.mask === 10 ? '50% 20%' : '45% 55% 38% 62%',
    transform: `translate(-50%, -50%) rotate(${chip.variant * 35}deg)`,
    background: `linear-gradient(135deg, ${biome.surfaceRaised}, ${biome.biome}${glow})`,
    opacity: unlocked ? 0.76 : 0.28,
  }
  const accent = (() => {
    if (biomeId === 'forest') return <span className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: biome.biomeSoft }} />
    if (biomeId === 'cave') return <span className="absolute left-1/2 top-1/2 h-4 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-sm" style={{ background: biome.biomeSoft, boxShadow: `0 0 8px ${biome.biome}` }} />
    if (biomeId === 'lava') return <span className="absolute left-1/2 top-1/2 h-1 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: '#ff9a45', boxShadow: '0 0 8px #ff6a45' }} />
    if (biomeId === 'ice') return <span className="absolute left-1/2 top-1/2 h-3 w-4 -translate-x-1/2 -translate-y-1/2 rounded-t-full" style={{ background: '#f4fbff' }} />
    if (biomeId === 'magical') return <span className="absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-sm" style={{ border: `2px solid ${biome.biomeSoft}`, boxShadow: `0 0 10px ${biome.biome}` }} />
    return <span className="absolute left-1/2 top-1/2 h-4 w-2 -translate-x-1/2 -translate-y-1/2 rounded-sm" style={{ background: '#d0a24c' }} />
  })()

  return (
    <span className="absolute h-7 w-7 -translate-x-1/2 -translate-y-1/2 overflow-hidden shadow-emboss" style={baseStyle}>
      {accent}
    </span>
  )
}

export function WorldMapScreen() {
  const { startLevel } = useNavigation()
  const { save } = useProgress()
  const reduced = useReducedMotion()

  const totalStars = LEVELS.reduce((sum, l) => sum + (save.progress.levelStars[l.index] ?? 0), 0)

  return (
    <ScreenShell title="World Map">
      <div className="flex flex-col gap-5">
        {BIOME_ORDER.map((id: BiomeId, biomeIdx) => {
          const biome = BIOMES[id]
          const levels = levelsByBiome(id)
          if (levels.length === 0) return null
          const unlocked = save.progress.unlockedBiomes.includes(id)
          const stars = levels.reduce((sum: number, l) => sum + (save.progress.levelStars[l.index] ?? 0), 0)
          const gate = levels[0]?.minStars ?? 0
          const starsToGo = Math.max(0, gate - totalStars)
          const art = biomeArt(id)
          const maxStars = levels.length * 3
          const cleared = save.progress.completedLevels.filter((li) => levels.some((l) => l.index === li)).length
          const mapCraft = buildBiomeMapCraft(levels, save.progress, unlocked, id)
          const compactNodes = levels.length > 16

          return (
            <motion.div
              key={id}
              layout
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: reduced ? 0 : Math.min(biomeIdx * 0.06, 0.3), type: 'spring', stiffness: 260, damping: 30 }}
              className="relative overflow-hidden rounded-[var(--r-xl)] border shadow-raised"
              style={{ borderColor: unlocked ? biome.edge : 'var(--edge)' }}
            >
              <div className="relative h-36 sm:h-44 w-full overflow-hidden">
                {art && (
                  <img
                    src={art}
                    alt={biome.name}
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ filter: unlocked ? 'saturate(1.05)' : 'grayscale(0.7) brightness(0.5)' }}
                  />
                )}
                <div
                  className="absolute inset-0"
                  style={{
                    background: `linear-gradient(180deg, rgba(8,6,14,0.15) 0%, rgba(8,6,14,0.35) 55%, ${biome.surface} 100%)`,
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 p-3.5">
                  <div className="flex items-center gap-2">
                    {!unlocked && (
                      <span className="grid h-7 w-7 place-items-center rounded-full bg-black/45 backdrop-blur-sm">
                        <PixelIcon name="lock" size={15} />
                      </span>
                    )}
                    <div>
                      <h2
                        className="font-display text-xl font-black leading-none tracking-tight"
                        style={{ color: '#fff', textShadow: '0 2px 10px rgba(0,0,0,0.7)' }}
                      >
                        {biome.name}
                      </h2>
                      {unlocked && (
                        <span className="mt-1 block text-[11px] font-semibold text-white/70">
                          {cleared}/{levels.length} cleared
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    className="flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-black backdrop-blur-sm"
                    style={{ background: 'rgba(0,0,0,0.4)', color: 'var(--coin)' }}
                  >
                    <PixelIcon name="star" size={14} />
                    {stars}/{maxStars}
                  </span>
                </div>
              </div>

              <div className="relative p-3.5" style={{ background: biome.surface, opacity: unlocked ? 1 : 0.75 }}>
                {!unlocked && starsToGo > 0 && (
                  <div className="mb-3 flex items-center gap-1.5 rounded-full bg-black/25 px-3 py-1.5 text-xs font-semibold text-[var(--text-dim)]">
                    <PixelIcon name="star" size={13} />
                    Earn {starsToGo} more {starsToGo === 1 ? 'star' : 'stars'} to light this region
                  </div>
                )}

                <div
                  className="relative h-40 overflow-hidden rounded-[var(--r-md)] border shadow-sunken"
                  style={{ borderColor: `${biome.edge}66`, background: `radial-gradient(circle at 50% 50%, ${biome.surfaceRaised}, ${biome.surface})` }}
                >
                  {mapCraft.chips.map((chip) => (
                    <MapChip key={chip.key} chip={chip} biomeId={id} biome={biome} unlocked={unlocked} />
                  ))}
                  {mapCraft.bridges.map((bridge) => (
                    <MapBridge key={bridge.key} bridge={bridge} biome={biome} />
                  ))}
                  {mapCraft.nodes.map((node) => (
                    <MapNode key={node.levelIndex} node={node} level={levels[node.order]} biome={biome} onStart={startLevel} compact={compactNodes} />
                  ))}
                </div>
              </div>
            </motion.div>
          )
        })}
      </div>
    </ScreenShell>
  )
}
